package com.exura.pages.planCreator.components

import androidx.compose.runtime.*
import com.exura.components.Icon
import com.exura.components.buttons.AsyncButton
import com.exura.features.plandefinition.domain.builder.plan.BasePlanBuilderNew
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorInputMode
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorTextArea
import com.exura.pages.planCreator.components.seriesViews.inputs.PlanCreatorTextInput
import com.exura.ui.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/**
 * Enhanced top bar for the plan canvas containing view controls, title/description, and action button
 */
@Composable
fun CanvasTopBar(
    title: String,
    description: String,
    planBuilder: BasePlanBuilderNew<out BasePlan>,
    displayMode: DisplayMode,
    onDisplayModeChange: (DisplayMode) -> Unit,
    viewMode: ViewMode,
    onViewModeChange: (ViewMode) -> Unit,
    onActionClick: () -> Unit,
    onTitleChange: (String) -> Unit,
    onDescriptionChange: (String) -> Unit,
    isDescriptionExpanded: Boolean = remember { mutableStateOf(false) }.value,
    isChanged: Boolean,
    isValid: Boolean,
) {
    var isDescriptionHovered by remember { mutableStateOf(false) }
    var internalDescriptionExpanded by remember { mutableStateOf(isDescriptionExpanded) }

    LaunchedEffect(isDescriptionExpanded) {
        internalDescriptionExpanded = isDescriptionExpanded
    }

    val inputMode = if (viewMode == ViewMode.EDIT) PlanCreatorInputMode.EDIT else PlanCreatorInputMode.VIEW

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(ExuraColors.WHITE)
            .padding(16.px)
    ) {
        // Top row with mode toggles, title, and action button
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left side - Mode toggles
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Display mode toggle
                ModeToggleGroup(
                    selectedMode = displayMode,
                    modes = listOf(
                        ModeToggleItem(DisplayMode.TEXT, "Tekst", Assets.IconsUntiled.PREVIEW_TEXT),
                        ModeToggleItem(DisplayMode.GRAPHICS, "Grafika", Assets.IconsUntiled.TIMELINE)
                    ),
                    onModeChange = onDisplayModeChange
                )

                // Spacer
                Box(modifier = Modifier.width(24.px))

                // View mode toggle
                ModeToggleGroup(
                    selectedMode = viewMode,
                    modes = listOf(
                        ModeToggleItem(ViewMode.EDIT, "Edycja", Assets.IconsUntiled.EDIT),
                        ModeToggleItem(ViewMode.VIEW, "Podgląd", Assets.IconsUntiled.EYE_OPEN)
                    ),
                    onModeChange = onViewModeChange
                )
            }

            // Center - Title input (takes remaining space)
            Box(
                modifier = Modifier
                    .weight(1f)
                    .margin(leftRight = 32.px)
            ) {
                PlanCreatorTextInput(
                    value = title,
                    onValueChange = onTitleChange,
                    mode = inputMode,
                    placeholder = "Wprowadź tytuł planu...",
                    textStyle = TextStyles.medium32,
                    textHeight = 32.px,
                    modifier = Modifier.fillMaxWidth(),
                )
            }

            // Right side - Action button and description toggle
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Description expand/collapse toggle
                Box(
                    modifier = Modifier
                        .padding(12.px)
                        .borderRadius(8.px)
                        .backgroundColor(ExuraColors.LIGHT_GRAY.copy(alpha = 0.3))
                        .cursor(Cursor.Pointer)
                        .transition(
                            Transition.of(
                                property = "background-color",
                                duration = Durations.short,
                                timingFunction = AnimationTimingFunction.EaseInOut
                            )
                        )
                        .onMouseEnter { isDescriptionHovered = true }
                        .onMouseLeave { isDescriptionHovered = false }
                        .textAlign(TextAlign.Center)
                        .let {mod ->
                            if (isDescriptionHovered) {
                                mod
                                    .backgroundColor(ExuraColors.LIGHT_GRAY.copy(alpha = 0.5))
                            } else {
                                mod
                            }
                        }
                        .onClick {
                            internalDescriptionExpanded = !internalDescriptionExpanded
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        iconSrc = if (internalDescriptionExpanded) Assets.IconsUntiled.COLLAPSE_TOP_BAR else Assets.IconsUntiled.EXPAND_TOP_BAR,
                        size = 16.px,
                        color = ExuraColors.ARSENIC,
                    )
                }

                AsyncButton(
                    text = if (viewMode == ViewMode.EDIT) "Zapisz" else "Prześlij",
                    onClick = onActionClick,
                    isDisabled = if (viewMode == ViewMode.EDIT) !isValid || !isChanged else !isValid
                )
            }
        }

        // Description section (collapsible)
        if (internalDescriptionExpanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 16.px)
                    .transition(
                        Transition.of(
                            property = "opacity",
                            duration = Durations.short,
                            timingFunction = AnimationTimingFunction.EaseInOut
                        )
                    )
            ) {
                PlanCreatorTextArea(
                    value = description,
                    onValueChange = onDescriptionChange,
                    mode = inputMode,
                    placeholder = "Wprowadź opis planu...",
                    maxLines = 3,
                    textStyle = TextStyles.normal14,
                    textHeight = 14.px,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}

// Keep the existing ModeToggleGroup and ModeToggleItem from the original implementation
// (assuming they exist in the original CanvasTopBar)

data class ModeToggleItem<T>(
    val mode: T,
    val label: String,
    val iconSrc: String
)

@Composable
fun <T> ModeToggleGroup(
    selectedMode: T,
    modes: List<ModeToggleItem<T>>,
    onModeChange: (T) -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .borderRadius(8.px)
            .border {
                width(Dimens.separatorWidthDefault)
                style(LineStyle.Solid)
                color(ExuraColors.LIGHT_GRAY)
            }
            .backgroundColor(ExuraColors.WHITE),
        horizontalArrangement = Arrangement.spacedBy(Dimens.separatorWidthDefault)
    ) {
        modes.forEach { item ->
            Box(
                modifier = Modifier
                    .padding(8.px)
                    .borderRadius(8.px)
                    .backgroundColor(
                        if (selectedMode == item.mode) ExuraColors.BLUE.copy(alpha = 0.1)
                        else ExuraColors.TRANSPARENT
                    )
                    .cursor(Cursor.Pointer)
                    .transition(
                        Transition.of(
                            property = "background-color",
                            duration = Durations.short,
                            timingFunction = AnimationTimingFunction.EaseInOut
                        )
                    )
                    .onClick { onModeChange(item.mode) },
                contentAlignment = Alignment.Center
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(4.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        iconSrc = item.iconSrc,
                        color = if (selectedMode == item.mode) ExuraColors.BLUE
                            else ExuraColors.ARSENIC
                    )

                    SpanText(
                        text = item.label,
                        modifier = Modifier
                            .classNames(TextStyles.medium12)
                            .color(
                                if (selectedMode == item.mode) ExuraColors.BLUE
                                else ExuraColors.ARSENIC
                            )
                    )
                }
            }
        }
    }
}
